import React, { useState } from 'react';
import { 
  MessageSquareWarning, 
  ShieldAlert, 
  CheckCircle2, 
  AlertTriangle, 
  ExternalLink, 
  PhoneCall, 
  Sparkles, 
  ArrowRight, 
  Copy, 
  Check, 
  AlertCircle,
  FileText
} from 'lucide-react';
import { analyzeScamMessage } from '../api/scams';
import { ScamAnalysisResult } from '../types';
import { SCAM_PRESETS } from '../data/scamScenarios';

export const ScamCheckerPage: React.FC = () => {
  const [content, setContent] = useState('');
  const [channel, setChannel] = useState<'sms' | 'email' | 'message' | 'job_offer' | 'invoice' | 'marketplace' | 'social_media'>('sms');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ScamAnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleAnalyze = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!content.trim()) return;

    setLoading(true);
    setError(null);
    try {
      const res = await analyzeScamMessage(content.trim(), channel);
      setResult(res);
    } catch (err: any) {
      setError(err.message || 'Error analyzing message snippet.');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadPreset = (presetId: string) => {
    const preset = SCAM_PRESETS.find(p => p.id === presetId);
    if (!preset) return;
    setContent(preset.fullContent);
    setChannel(preset.channel);
    setResult(null);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-950 text-amber-300 text-xs font-semibold border border-amber-800">
          <MessageSquareWarning className="w-3.5 h-3.5" />
          <span>AI Scam & Phishing Analyzer</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-white">
          Does this look like a scam?
        </h1>
        <p className="text-slate-400 text-sm max-w-xl mx-auto">
          Paste any suspicious SMS, toll notice, tax refund email, PayID message, or marketplace enquiry to get an immediate risk evaluation.
        </p>
      </div>

      {/* Main Analysis Form Card */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6">
        {/* Channel Selector */}
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-800 pb-4">
          <span className="text-xs font-bold text-slate-400 uppercase tracking-wider mr-2">Channel:</span>
          {(['sms', 'email', 'message', 'job_offer', 'invoice', 'marketplace', 'social_media'] as const).map((ch) => (
            <button
              key={ch}
              type="button"
              onClick={() => setChannel(ch)}
              className={`px-3 py-1 rounded-lg text-xs font-medium uppercase tracking-wide transition-colors ${
                channel === ch ? 'bg-amber-500 text-slate-950 font-bold' : 'bg-slate-800 text-slate-300 hover:text-white'
              }`}
            >
              {ch.replace('_', ' ')}
            </button>
          ))}
        </div>

        {/* Text Input */}
        <form onSubmit={handleAnalyze} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
              Paste the message or conversation snippet here:
            </label>
            <textarea
              rows={5}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="e.g. AusPost: Your parcel AU-88219 is on hold due to $2.35 unpaid fee. Update your details at..."
              className="w-full p-4 rounded-xl bg-slate-950 border border-slate-700 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-amber-500 font-sans shadow-inner leading-relaxed"
            />
          </div>

          {/* Quick preset loader */}
          <div className="space-y-2">
            <span className="text-xs text-slate-400 font-medium">Or load a known Australian scam example:</span>
            <div className="flex flex-wrap gap-2">
              {SCAM_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => handleLoadPreset(preset.id)}
                  className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs transition-colors border border-slate-700/60 font-medium"
                >
                  {preset.title}
                </button>
              ))}
            </div>
          </div>

          <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-500">
              Never paste personal passwords or sensitive un-redacted documents.
            </p>
            <button
              type="submit"
              disabled={loading || !content.trim()}
              className="w-full sm:w-auto px-7 py-3 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-950 rounded-xl font-bold text-sm shadow-lg shadow-amber-950/30 transition-all flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
                  <span>Analyzing Indicators...</span>
                </>
              ) : (
                <>
                  <MessageSquareWarning className="w-4 h-4" />
                  <span>Check for Scam</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-rose-950/80 border border-rose-800 text-rose-200 text-sm flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* RESULTS BOX */}
      {result && (
        <div className="space-y-8 animate-fade-in">
          {/* Main Risk Status Banner */}
          <div className={`p-6 sm:p-8 rounded-3xl border shadow-2xl space-y-4 ${
            result.riskLevel === 'CRITICAL'
              ? 'bg-rose-950/40 border-rose-700 text-rose-200'
              : result.riskLevel === 'HIGH'
                ? 'bg-orange-950/40 border-orange-700 text-orange-200'
                : result.riskLevel === 'MEDIUM'
                  ? 'bg-amber-950/40 border-amber-700 text-amber-200'
                  : 'bg-emerald-950/40 border-emerald-700 text-emerald-200'
          }`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800/80 pb-4">
              <div>
                <span className="text-xs uppercase font-bold tracking-wider opacity-80">AI Classification Result</span>
                <h3 className="text-2xl font-extrabold text-white">
                  Scam Risk: {result.riskLevel} RISK ({result.riskScore}%)
                </h3>
              </div>
              <div className="text-right">
                <span className="text-xs opacity-75 font-mono">Channel: {result.channel.toUpperCase()}</span>
              </div>
            </div>

            {/* Why section */}
            <div className="space-y-2">
              <h4 className="text-sm font-bold text-white uppercase tracking-wider">
                Why was this rated {result.riskLevel}?
              </h4>
              <ul className="space-y-1.5 text-xs text-slate-200">
                {result.reasons.map((reason, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-amber-400 font-bold">•</span>
                    <span>{reason}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Detected Indicators Checklist */}
            <div className="pt-2">
              <span className="text-xs font-bold text-slate-300 uppercase tracking-wider block mb-2">
                Detected Threat Signatures:
              </span>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                <div className={`p-2 rounded-lg border ${result.detectedIndicators.urgency ? 'bg-rose-950/60 border-rose-800 text-rose-300' : 'bg-slate-900 border-slate-800 text-slate-500'}`}>
                  {result.detectedIndicators.urgency ? '⚠️ Artificial Urgency' : '✓ Normal Timetable'}
                </div>
                <div className={`p-2 rounded-lg border ${result.detectedIndicators.suspiciousPayment ? 'bg-rose-950/60 border-rose-800 text-rose-300' : 'bg-slate-900 border-slate-800 text-slate-500'}`}>
                  {result.detectedIndicators.suspiciousPayment ? '⚠️ Suspicious Payment Request' : '✓ No Payment Push'}
                </div>
                <div className={`p-2 rounded-lg border ${result.detectedIndicators.suspiciousLink ? 'bg-rose-950/60 border-rose-800 text-rose-300' : 'bg-slate-900 border-slate-800 text-slate-500'}`}>
                  {result.detectedIndicators.suspiciousLink ? '⚠️ Non-Official Link' : '✓ No Malicious Links'}
                </div>
                <div className={`p-2 rounded-lg border ${result.detectedIndicators.sensitiveInfoRequest ? 'bg-rose-950/60 border-rose-800 text-rose-300' : 'bg-slate-900 border-slate-800 text-slate-500'}`}>
                  {result.detectedIndicators.sensitiveInfoRequest ? '⚠️ Solicits ID / Passwords' : '✓ No Credential Solicitation'}
                </div>
                <div className={`p-2 rounded-lg border ${result.detectedIndicators.senderMismatch ? 'bg-rose-950/60 border-rose-800 text-rose-300' : 'bg-slate-900 border-slate-800 text-slate-500'}`}>
                  {result.detectedIndicators.senderMismatch ? '⚠️ Brand Impersonation' : '✓ Sender Consistent'}
                </div>
              </div>
            </div>
          </div>

          {/* What Should You Do Next? */}
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-4 shadow-xl">
            <h4 className="text-base font-bold text-white flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-400" />
              <span>What should you do?</span>
            </h4>
            <div className="space-y-2.5">
              {result.recommendedActions.map((action, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 rounded-xl bg-slate-950 border border-slate-800 text-xs text-slate-200">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{action}</span>
                </div>
              ))}
            </div>

            {/* Official reporting hotlinks */}
            <div className="pt-4 border-t border-slate-800 flex flex-wrap items-center justify-between gap-3 text-xs">
              <div className="text-slate-400">
                Help protect other Australians by reporting this scam:
              </div>
              <div className="flex items-center gap-3">
                <a
                  href={result.officialReporting.scamwatchUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white flex items-center gap-1 transition-colors"
                >
                  <span>Scamwatch (ACCC)</span>
                  <ExternalLink className="w-3 h-3 text-slate-400" />
                </a>
                <a
                  href={`tel:${result.officialReporting.idcarePhone.replace(/\s+/g, '')}`}
                  className="px-3 py-1.5 rounded-lg bg-emerald-950 border border-emerald-800 text-emerald-300 hover:bg-emerald-900 flex items-center gap-1 font-mono transition-colors"
                >
                  <PhoneCall className="w-3 h-3" />
                  <span>IDCARE 1800 595 160</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
